//* 动画库
import gsap from "gsap";
//* 全局属性
import { UiTypes } from "@various/constants";
//* 工具函数
import { useElementOption } from "./useElementOption";
import { disposeMainAxis, disposeSubAxis, setTriangleAttr } from "../utils";
import { isNumber } from "../../../utility";
//* 配置项
import setting from "../setting";

/**@name 窗口相对于容器定位的可视区域边界算法的节点 */
export type RelativeContainerBodyNodes = {
    container: HTMLElement; //* 相对的容器节点
    triangle?: HTMLElement; //* 窗口上方的三角形节点
    view: HTMLElement; //* 窗口节点
};

/**@name 窗口相对于容器定位的可视区域边界算法的配置项 */
export type RelativeContainerBodyOption = {
    //* 副轴对齐方式
    align: UiTypes.align;
    //* 主轴偏移位置
    offset: number;
    //* 主轴方向
    direction: UiTypes.direction;
    //* 窗口宽度
    width?: number | { min?: number; max?: number };
    //* 窗口高度
    height?: number;
    /**
     * * 边界位置，{ x：X轴边界, y: Y轴边界, strategy: 处理策略 }
     * * strategy: Default, 超出边界时若对轴空间充足则反方向定位
     * * strategy: Perfect, 尽可能展示完整, 但会导致小三角位置错位
     * * strategy: Disable, 禁用边界处理函数
     */
    boundary?: { x?: { min?: number; max?: number }; y?: { min?: number; max?: number }; strategy?: "Perfect" | "Disable" | "Default" };
    //* 是否过渡动画
    animation?: boolean;
};

/**@name 窗口相对于容器定位的可视区域边界算法的结果 */
export type RelativeContainerBodyResult = {
    //* 副轴对齐方式
    align: UiTypes.align;
    //* 偏移位置
    offsetX: number;
    offsetY: number;
    //* 主轴对齐方式
    direction: UiTypes.direction;
};

/**
 * @name 窗口相对于容器定位的可视区域边界算法（设置）
 * @param container 容器
 * @param view 窗口
 * @param option
 * @returns
 */
export const relativeContainerBody = (nodes: RelativeContainerBodyNodes, option: RelativeContainerBodyOption) => {
    //* 边界坐标处理
    const data = relativeContainerBodySite(nodes, option);
    //* 属性设置
    if (option.animation) {
        gsap.to(nodes.view, { top: data.offsetY, left: data.offsetX, duration: 0.2 });
    } else {
        nodes.view.style.transformOrigin = `${setting.DIRECTION[data.direction]} center`;
        nodes.view.style.left = `${data.offsetX}px`;
        nodes.view.style.top = `${data.offsetY}px`;
    }

    //* 小三角位置调整
    if (nodes.triangle) {
        setTriangleAttr(nodes.triangle, data.direction, data.align);
    }
};

/**
 * @name 窗口相对于容器定位的可视区域边界算法（坐标）
 * @param container 容器
 * @param view 窗口
 * @param option
 */
export const relativeContainerBodySite = (nodes: RelativeContainerBodyNodes, option: RelativeContainerBodyOption): RelativeContainerBodyResult => {
    //* 获取容器位置参数
    const rect = useElementOption(nodes.container);
    //* 边界初始化
    const offsetYMin = option.boundary?.y?.min || 0;
    const offsetYMax = option.boundary?.y?.max || innerHeight;
    const offsetXMin = option.boundary?.x?.min || 0;
    const offsetXMax = option.boundary?.x?.max || innerWidth;
    //* 窗口尺寸调整
    if (option.height) nodes.view.style.maxHeight = option.height + "px";
    if (option.width) {
        if (isNumber(option.width)) {
            nodes.view.style.width = option.width + "px";
            nodes.view.style.minWidth = "";
            nodes.view.style.maxWidth = "";
        } else {
            //* 最小尺寸设置
            if (option.width.min) nodes.view.style.minWidth = option.width.min + "px";
            else {
                nodes.view.style.minWidth = "";
            }

            //* 最大尺寸设置
            if (option.width.max) nodes.view.style.maxWidth = option.width.max + "px";
            else {
                nodes.view.style.maxWidth = "";
            }
        }
    }

    switch (option.direction) {
        case "top": {
            //* 初始化位置
            const offest = { offsetX: rect.rootX, offsetY: Math.floor(rect.rootY - option.offset - nodes.view.offsetHeight) };
            //* 计算主轴位置
            const mainAxis = disposeMainAxis(
                { container: rect.height, space: option.offset, view: nodes.view.offsetHeight },
                {
                    min: offsetYMin + scrollY,
                    max: offsetYMax + scrollY,
                    offset: offest.offsetY,
                    strategy: option.boundary?.strategy,
                    direction: option.direction,
                    orientation: -1,
                }
            );

            //* 计算副轴位置
            const subAxis = disposeSubAxis(
                { container: rect.width, space: 0, view: nodes.view.offsetWidth },
                {
                    min: offsetXMin + scrollX,
                    max: offsetXMax + scrollX,
                    align: option.align,
                    offset: offest.offsetX,
                    strategy: option.boundary?.strategy,
                }
            );

            //* 退出
            return { offsetY: mainAxis.offset, offsetX: subAxis.offset, align: subAxis.align, direction: mainAxis.direction };
        }

        case "left": {
            //* 初始化位置
            const offest = { offsetX: Math.floor(rect.rootX - option.offset - nodes.view.offsetWidth), offsetY: rect.rootY };
            //* 计算主轴位置
            const mainAxis = disposeMainAxis(
                { container: rect.width, space: option.offset, view: nodes.view.offsetWidth },
                {
                    min: offsetXMin + scrollX,
                    max: offsetXMax + scrollX,
                    offset: offest.offsetX,
                    strategy: option.boundary?.strategy,
                    direction: option.direction,
                    orientation: -1,
                }
            );

            //* 计算副轴位置
            const subAxis = disposeSubAxis(
                { container: rect.height, space: 0, view: nodes.view.offsetHeight },
                {
                    min: offsetYMin + scrollY,
                    max: offsetYMax + scrollY,
                    align: option.align,
                    offset: offest.offsetY,
                    strategy: option.boundary?.strategy,
                }
            );

            //* 退出
            return { offsetY: subAxis.offset, offsetX: mainAxis.offset, align: subAxis.align, direction: mainAxis.direction };
        }

        case "right": {
            //* 初始化位置
            const offest = { offsetX: Math.floor(rect.rootX + rect.width + option.offset), offsetY: rect.rootY };

            //* 计算主轴位置
            const mainAxis = disposeMainAxis(
                { container: rect.width, space: option.offset, view: nodes.view.offsetWidth },
                {
                    min: offsetXMin + scrollX,
                    max: offsetXMax + scrollX,
                    offset: offest.offsetX,
                    strategy: option.boundary?.strategy,
                    direction: option.direction,
                    orientation: 1,
                }
            );

            //* 计算副轴位置
            const subAxis = disposeSubAxis(
                { container: rect.height, space: 0, view: nodes.view.offsetHeight },
                {
                    min: offsetYMin + scrollY,
                    max: offsetYMax + scrollY,
                    align: option.align,
                    offset: offest.offsetY,
                    strategy: option.boundary?.strategy,
                }
            );

            //* 退出
            return { offsetY: subAxis.offset, offsetX: mainAxis.offset, align: subAxis.align, direction: mainAxis.direction };
        }

        case "bottom": {
            //* 初始化位置
            const offest = { offsetX: rect.rootX, offsetY: Math.floor(rect.rootY + rect.height + option.offset) };

            //* 计算主轴位置
            const mainAxis = disposeMainAxis(
                { container: rect.height, space: option.offset, view: nodes.view.offsetHeight },
                {
                    min: offsetYMin + scrollY,
                    max: offsetYMax + scrollY,
                    offset: offest.offsetY,
                    strategy: option.boundary?.strategy,
                    direction: option.direction,
                    orientation: 1,
                }
            );

            //* 计算副轴位置
            const subAxis = disposeSubAxis(
                { container: rect.width, space: 0, view: nodes.view.offsetWidth },
                {
                    min: offsetXMin + scrollX,
                    max: offsetXMax + scrollX,
                    align: option.align,
                    offset: offest.offsetX,
                    strategy: option.boundary?.strategy,
                }
            );

            //* 退出
            return { offsetY: mainAxis.offset, offsetX: subAxis.offset, align: subAxis.align, direction: mainAxis.direction };
        }

        default: {
            return { offsetY: 0, offsetX: 0, align: option.align, direction: "top" };
        }
    }
};
